/**
 * useBrokerBookTrend — thin httpsCallable wrapper for `getBrokerBookTrend`.
 *
 * Earnout Tracker design — only companyId is required; the callable resolves
 * the previous owner from tenant config and defaults periodMonth to most recent.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type {
  GetBrokerBookTrendRequest,
  GetBrokerBookTrendResponse,
  SetLoanDispositionRequest,
  SetLoanDispositionResponse,
  ManualDisposition,
} from '../types/brokerBookTrend';

export interface UseBrokerBookTrendParams {
  companyId: string | null;
  /** Optional override — defaults to the most recent month with data on the backend. */
  periodMonth?: string | null;
}

export interface UseBrokerBookTrendReturn {
  data: GetBrokerBookTrendResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useBrokerBookTrend(
  params: UseBrokerBookTrendParams,
): UseBrokerBookTrendReturn {
  const { companyId, periodMonth } = params;

  const [data, setData] = useState<GetBrokerBookTrendResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const requestIdRef = useRef<number>(0);

  const fetchOnce = useCallback(async (): Promise<void> => {
    if (!companyId) return;

    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const callable = httpsCallable<
        GetBrokerBookTrendRequest,
        GetBrokerBookTrendResponse
      >(functions, 'getBrokerBookTrend');
      const payload: GetBrokerBookTrendRequest = { companyId };
      if (periodMonth) payload.periodMonth = periodMonth;
      const response = await callable(payload);
      if (myRequestId !== requestIdRef.current) return;
      setData(response.data);
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      console.error('[useBrokerBookTrend] callable error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setData(null);
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false);
    }
  }, [companyId, periodMonth]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const refetch = useCallback(async (): Promise<void> => {
    await fetchOnce();
  }, [fetchOnce]);

  return { data, loading, error, refetch };
}

export default useBrokerBookTrend;

// ============================================================================
// useSetLoanDisposition — manual override hook
// ============================================================================

export interface UseSetLoanDispositionReturn {
  setDisposition: (
    args: { loanId: string; disposition: ManualDisposition; note?: string },
  ) => Promise<SetLoanDispositionResponse>;
  saving: boolean;
  error: Error | null;
}

export function useSetLoanDisposition(
  companyId: string | null,
): UseSetLoanDispositionReturn {
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const setDisposition: UseSetLoanDispositionReturn['setDisposition'] = useCallback(
    async ({ loanId, disposition, note }) => {
      if (!companyId) throw new Error('companyId required');
      setSaving(true);
      setError(null);
      try {
        const callable = httpsCallable<
          SetLoanDispositionRequest,
          SetLoanDispositionResponse
        >(functions, 'setLoanDisposition');
        const payload: SetLoanDispositionRequest = { companyId, loanId, disposition };
        if (note) payload.note = note;
        const res = await callable(payload);
        return res.data;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [companyId],
  );

  return { setDisposition, saving, error };
}
